import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Typography, Button, TextField, Box, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Checkbox, FormControlLabel, Alert, CircularProgress 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../auth/Auth';

const ContractorCharacterReplacementPage = () => {
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    name: '',
    phrase: '',
    syrveEdrpou: ''
  });
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [replacements, setReplacements] = useState({});
  const [newReplacement, setNewReplacement] = useState({
    name: '',
    phrase: '',
    syrveEdrpou: '',
    contractorId: '',
    status: 1
  });
  const [multiplePhrases, setMultiplePhrases] = useState([{ phrase: '' }]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReplacement, setEditingReplacement] = useState(null);
  const [existingPhraseMode, setExistingPhraseMode] = useState(false);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [selectedGroupStatus, setSelectedGroupStatus] = useState(1);
  const [selectedGroupSyrve, setSelectedGroupSyrve] = useState('');
  const [translations, setTranslations] = useState(null);
  const [language, setLanguage] = useState('uk-UA');
  const { user } = useAuth();

  useEffect(() => {
    if (user?.user_id) {
      fetchContractorData();
    }
  }, [user]);

  const fetchContractorData = async () => {
    try {
      const userId = user?.user_id;
      if (!userId) {
        setGeneralError('Користувач не авторизований');
        setLoading(false);
        return;
      }

      const regionResponse = await axios.get(`https://bank2.daolog.net/user-region/${userId}`);
      const userRegion = regionResponse.data.region;
      setLanguage(userRegion);

      const translationsResponse = await axios.get(`https://bank2.daolog.net/translations/contractor-char-replacements/${userRegion}`);
      setTranslations(translationsResponse.data);

      const contractorResponse = await axios.get(`https://bank2.daolog.net/user-contractor-mmm/${userId}`);
      const contractorData = contractorResponse.data;
      setContractor(contractorData);
      setNewReplacement(prev => ({ ...prev, contractorId: contractorData.id }));

      if (contractorData.id) {
        await fetchReplacements(contractorData.id);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching contractor data:', error);
      setGeneralError('Помилка при отриманні даних');
      setLoading(false);
    }
  };

  const fetchReplacements = async (contractorId) => {
    try {
      const response = await axios.get(`https://bank2.daolog.net/contractor-char-replacements/${contractorId}`);
      const data = response.data;
      
      const grouped = {};
      data.forEach(group => {
        if (group.replacements && Array.isArray(group.replacements)) {
          grouped[group.name] = {
            syrve_edrpou: group.syrve_edrpou || '',
            status: parseInt(group.status),
            replacements: group.replacements.map(rep => ({
              id: rep.original,
              name: group.name,
              phrase: rep.original,
              replacement: rep.replacement,
              status: parseInt(group.status),
              syrve_edrpou: group.syrve_edrpou || ''
            }))
          };
        }
      });
      
      setReplacements(grouped);
    } catch (error) {
      console.error('Error fetching replacements:', error);
      setGeneralError(translations?.errorFetchingReplacements || 'Помилка при отриманні фраз');
    }
  };

  const handlePhraseChange = (index, value) => {
    const newPhrases = [...multiplePhrases];
    newPhrases[index].phrase = value;
    setMultiplePhrases(newPhrases);
  };

  const handleAddPhrase = () => {
    setMultiplePhrases([...multiplePhrases, { phrase: '' }]);
  };

  const handleRemovePhrase = (index) => {
    const newPhrases = multiplePhrases.filter((_, i) => i !== index);
    setMultiplePhrases(newPhrases);
  };

  const handleOpenEditDialog = (replacement) => {
    setEditingReplacement({ ...replacement });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditingReplacement(null);
    setEditDialogOpen(false);
  };

  const handleExistingPhraseClick = (groupName, groupStatus, groupSyrve) => {
    setExistingPhraseMode(true);
    setSelectedGroupName(groupName);
    setSelectedGroupStatus(groupStatus);
    setSelectedGroupSyrve(groupSyrve || '');
    setNewReplacement(prev => ({
      ...prev,
      name: groupName,
      status: groupStatus,
      syrveEdrpou: groupSyrve || '',
      phrase: ''
    }));
    setMultiplePhrases([{ phrase: '' }]);
  };

  const handleCancelAddPhrase = () => {
    setExistingPhraseMode(false);
    setSelectedGroupName('');
    setSelectedGroupStatus(1);
    setSelectedGroupSyrve('');
    setNewReplacement({
      name: '',
      phrase: '',
      syrveEdrpou: '',
      contractorId: contractor?.id,
      status: 1
    });
    setMultiplePhrases([{ phrase: '' }]);
    setErrors({
      name: '',
      phrase: '',
      syrveEdrpou: ''
    });
    setGeneralError('');
  };

  const handleStatusChange = async (name, newStatus) => {
    try {
      const encodedName = encodeURIComponent(name);
      const response = await axios.put(`https://bank2.daolog.net/contractor-char-replacements/group-status/${contractor?.id}/${encodedName}`, {
        status: newStatus,
        userId: user?.user_id
      });

      if (response.data.success) {
        await fetchReplacements(contractor?.id);
        setSuccessMessage(translations?.statusUpdated || 'Статус оновлено');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating replacements status:', error);
      setGeneralError(translations?.errorUpdatingStatus || 'Помилка при оновленні статусу');
    }
  };

  const handleSyrveChange = async (name, newSyrve) => {
    try {
      const encodedName = encodeURIComponent(name);
      const response = await axios.put(`https://bank2.daolog.net/contractor-char-replacements/group-syrve/${contractor?.id}/${encodedName}`, {
        syrveEdrpou: newSyrve,
        userId: user?.user_id
      });

      if (response.data.success) {
        await fetchReplacements(contractor?.id);
        setSuccessMessage(translations?.syrveUpdated || 'Syrve ЄДРПОУ оновлено');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating syrve:', error);
      setGeneralError(translations?.errorUpdatingSyrve || 'Помилка при оновленні Syrve ЄДРПОУ');
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: '',
      phrase: '',
      syrveEdrpou: ''
    };

    if (!existingPhraseMode && !newReplacement.name.trim()) {
      newErrors.name = translations?.warning || 'Це поле обов\'язкове';
      isValid = false;
    }

    const emptyPhrases = multiplePhrases.some(p => !p.phrase.trim());
    if (emptyPhrases) {
      newErrors.phrase = translations?.warningPhrase || 'Ключова фраза обов\'язкова';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAddReplacement = async () => {
    if (!validateForm()) {
      return;
    }

    const validPhrases = multiplePhrases.filter(p => p.phrase.trim());
    
    if (validPhrases.length === 0) {
      setGeneralError(translations?.warningPhrase || 'Додайте хоча б одну ключову фразу');
      return;
    }

    try {
      const promises = validPhrases.map(phrase =>
        axios.post(`https://bank2.daolog.net/contractor-char-replacements`, {
          ...newReplacement,
          phrase: phrase.phrase,
          userId: user?.user_id,
          contractorId: contractor?.id
        })
      );

      const results = await Promise.all(promises);
      const allSuccess = results.every(result => result.data.success !== false);
      
      if (allSuccess) {
        setNewReplacement({
          name: '',
          phrase: '',
          syrveEdrpou: '',
          contractorId: contractor?.id,
          status: 1
        });
        setMultiplePhrases([{ phrase: '' }]);
        setErrors({
          name: '',
          phrase: '',
          syrveEdrpou: ''
        });
        setGeneralError('');
        setExistingPhraseMode(false);
        setSelectedGroupName('');
        setSelectedGroupSyrve('');
        
        await fetchReplacements(contractor?.id);
        setSuccessMessage(translations?.phraseAdded || 'Фразу додано');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setGeneralError(translations?.errorAddingReplacement || 'Помилка при додаванні деяких фраз');
      }
    } catch (error) {
      console.error('Error adding replacements:', error);
      const errorMessage = error.response?.data?.message || (translations?.errorAddingReplacement || 'Помилка при додаванні фрази');
      setGeneralError(errorMessage);
    }
  };

  const handleDeleteReplacement = async (name, phrase) => {
    try {
      const encodedName = encodeURIComponent(name);
      const encodedPhrase = encodeURIComponent(phrase);
      
      const response = await axios.delete(
        `https://bank2.daolog.net/contractor-char-replacements/${contractor?.id}/${encodedName}/${encodedPhrase}`,
        {
          data: { userId: user?.user_id }
        }
      );
      
      if (response.data.success) {
        await fetchReplacements(contractor?.id);
        setSuccessMessage(translations?.phraseDeleted || 'Фразу видалено');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting replacement:', error);
      setGeneralError(translations?.errorDeletingReplacement || 'Помилка при видаленні фрази');
    }
  };

  const handleDeleteGroup = async (name) => {
    const groupData = replacements[name];
    if (groupData && groupData.replacements && groupData.replacements.length > 0) {
      for (const replacement of groupData.replacements) {
        await handleDeleteReplacement(name, replacement.phrase);
      }
    }
    setSuccessMessage(translations?.groupDeleted || 'Групу видалено');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(`https://bank2.daolog.net/contractor-char-replacements/update/${contractor?.id}`, {
        id: editingReplacement.id,
        name: editingReplacement.name,
        phrase: editingReplacement.phrase,
        status: editingReplacement.status,
        syrveEdrpou: editingReplacement.syrve_edrpou,
        userId: user?.user_id
      });

      if (response.data.success) {
        await fetchReplacements(contractor?.id);
        handleCloseEditDialog();
        setSuccessMessage(translations?.updateSuccess || 'Фразу оновлено');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating replacement:', error);
      setGeneralError(translations?.errorUpdatingReplacement || 'Помилка при оновленні фрази');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (!translations) return <Typography>{translations?.loading || 'Loading...'}</Typography>;

  return (
    <Box sx={{ p: 3 }} style={{ marginTop: "120px" }}>
      <Typography variant="h6" gutterBottom>
        <b>{translations.client}:</b> {contractor?.name}
      </Typography>

      <Typography variant="h5" gutterBottom style={{ marginTop: "20px" }}>
        {translations.management}
      </Typography>

      {generalError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGeneralError('')}>
          {generalError}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 2 
        }}>
          <TextField
            required
            error={Boolean(errors.name)}
            label={translations.replaceName}
            value={newReplacement.name}
            onChange={(e) => {
              setNewReplacement({ ...newReplacement, name: e.target.value });
              setErrors({ ...errors, name: '' });
            }}
            disabled={existingPhraseMode}
            helperText={errors.name}
            sx={{ width: '400px' }}
          />
          
          <TextField
            label={translations.syrveEdrpou}
            value={newReplacement.syrveEdrpou}
            onChange={(e) => {
              setNewReplacement({ ...newReplacement, syrveEdrpou: e.target.value });
              setErrors({ ...errors, syrveEdrpou: '' });
            }}
            disabled={existingPhraseMode}
            helperText={errors.syrveEdrpou}
            sx={{ width: '400px' }}
          />
          
          {multiplePhrases.map((phrase, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '400px' }}>
              <TextField
                required
                error={Boolean(errors.phrase && !phrase.phrase.trim())}
                label={translations.keyPhrase}
                value={phrase.phrase}
                onChange={(e) => handlePhraseChange(index, e.target.value)}
                helperText={index === 0 && errors.phrase && !phrase.phrase.trim() ? errors.phrase : ''}
                sx={{ flex: 1 }}
              />
              {index === multiplePhrases.length - 1 ? (
                <IconButton onClick={handleAddPhrase} color="primary">
                  <AddIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => handleRemovePhrase(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReplacement}
            >
              {translations.add}
            </Button>
            {existingPhraseMode && (
              <Button
                variant="outlined"
                onClick={handleCancelAddPhrase}
              >
                {translations.cancel}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {Object.entries(replacements).length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {translations.noData || 'Немає доданих фраз'}
          </Typography>
        ) : (
          Object.entries(replacements).map(([name, groupData]) => (
            <Box key={name} sx={{ mb: 2 }}>
              <Box 
                sx={{ 
                  p: 1, 
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}
              >
                <Box 
                  sx={{ cursor: 'pointer', flex: 2 }}
                  onClick={() => handleExistingPhraseClick(name, groupData.status, groupData.syrve_edrpou)}
                >
                  <Typography><b>{translations.replaceName}:</b> {name}</Typography>
                  {groupData.syrve_edrpou && (
                    <Typography variant="body2" color="text.secondary">
                      <b>{translations.syrveEdrpou}</b> {groupData.syrve_edrpou}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Syrve ЄДРПОУ"
                    value={groupData.syrve_edrpou || ''}
                    onChange={(e) => handleSyrveChange(name, e.target.value)}
                    sx={{ width: '200px' }}
                    placeholder="Введіть ЄДРПОУ"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={groupData.status === 1}
                        onChange={(e) => handleStatusChange(name, e.target.checked ? 1 : 0)}
                      />
                    }
                    label={groupData.status === 1 ? translations.activeConfig : translations.inactiveConfig}
                  />
                  <IconButton 
                    onClick={() => {
                      if (window.confirm(translations?.confirmDeleteGroup || 'Ви впевнені, що хочете видалити всю групу?')) {
                        handleDeleteGroup(name);
                      }
                    }} 
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ pl: 2 }}>
                {groupData.replacements.map((replacement) => (
                  <Box key={replacement.id} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ flex: 1 }}>
                      <strong>{translations.keyPhrase}</strong> {replacement.phrase}<br/>
                 
                    </Typography>
                    <IconButton onClick={() => handleDeleteReplacement(name, replacement.phrase)} color="error">
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOpenEditDialog(replacement)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          ))
        )}
      </Paper>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {translations.edit}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={translations.replaceName}
              value={editingReplacement?.name || ''}
              onChange={(e) => setEditingReplacement({ ...editingReplacement, name: e.target.value })}
              fullWidth
            />
            <TextField
              label={translations.syrveEdrpou}
              value={editingReplacement?.syrve_edrpou || ''}
              onChange={(e) => setEditingReplacement({ ...editingReplacement, syrve_edrpou: e.target.value })}
              fullWidth
            />
            <TextField
              label={translations.keyPhrase}
              value={editingReplacement?.phrase || ''}
              onChange={(e) => setEditingReplacement({ ...editingReplacement, phrase: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingReplacement?.status === 1}
                  onChange={(e) => setEditingReplacement({ ...editingReplacement, status: e.target.checked ? 1 : 0 })}
                />
              }
              label={translations.activeConfig}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{translations.cancel}</Button>
          <Button onClick={handleSaveEdit} variant="contained">{translations.save}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorCharacterReplacementPage;